import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, TextField } from "@components/ui";
import { EnvelopeIcon, LockIcon, ShieldIcon, SlackIcon } from "@icons";
import { apiClient, ApiError } from "@lib/apiClient";
import styles from "./Auth.module.css";

interface LoginPageProps {
  onLogin: (token: string, user: { id: string; email: string; role: "SUPERADMIN" | "ADMIN" | "USER" }) => void;
}

type LoginUser = { id: string; email: string; role: "SUPERADMIN" | "ADMIN" | "USER" };

// /auth/login answers with a session on success, or — when the account has
// TOTP enabled — a short-lived challenge token to redeem via /auth/mfa/verify.
type LoginResponse =
  | { token: string; user: LoginUser }
  | { mfaRequired: true; mfaToken: string };

export function LoginPage({ onLogin }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Non-null once the password step succeeded on an MFA-enabled account: the
  // form switches to the 6-digit-code step until verify succeeds or Back.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Route through apiClient so it targets VITE_API_BASE (the Worker), not
      // the page's own origin.
      const data = await apiClient.post<LoginResponse>("/auth/login", { email, password });
      if ("mfaRequired" in data) {
        setMfaToken(data.mfaToken);
        setCode("");
      } else {
        onLogin(data.token, data.user);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  async function verifyMfa(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setLoading(true);
    try {
      const data = await apiClient.post<{ token: string; user: LoginUser }>("/auth/mfa/verify", {
        mfaToken,
        code,
      });
      onLogin(data.token, data.user);
    } catch (e) {
      // 401 "Invalid code" (or an expired challenge token) surfaces inline —
      // apiClient exempts /auth/mfa/verify from the session-expiry redirect.
      if (e instanceof ApiError && e.status === 401) setError(e.message || "Invalid code");
      else setError(e instanceof Error ? e.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  }

  function backToPassword() {
    setMfaToken(null);
    setCode("");
    setError(null);
  }

  return (
    <div className={styles.root}>
      <aside className={styles.hero}>
        <div className={styles.aurora} aria-hidden>
          <span className={`${styles.auroraGlow} ${styles.auroraGlowA}`} />
          <span className={`${styles.auroraGlow} ${styles.auroraGlowB}`} />
        </div>
        <div className={styles.grain} aria-hidden />
        <div className={styles.heroInner}>
          <img src="/icons/icon.svg" alt="" className={styles.heroLogo} width={96} height={96} />
          <div className={styles.heroBrand}>
            <div className={styles.heroTitle}>Aria</div>
            <div className={styles.heroKicker}>by Aethera Healthcare</div>
          </div>
          <p className={styles.heroTag}>
            Secure voice, video, and messaging between you and your care team.
          </p>
        </div>
        <div className={styles.heroFoot}>A product of Aethera Healthcare Solutions</div>
      </aside>
      <div className={styles.panel}>
        <div className={styles.card}>
          {mfaToken ? (
            <>
              <h1 className={styles.heading}>Two-step verification</h1>
              <p className={styles.sub}>Enter your 6-digit code from your authenticator app.</p>
              <form onSubmit={verifyMfa} className={styles.form}>
                <TextField
                  id="mfa-code"
                  type="text"
                  label="6-digit code"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  leading={<ShieldIcon />}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  required
                />
                {error && <p className={styles.error}>{error}</p>}
                <Button type="submit" fullWidth isLoading={loading} disabled={code.length !== 6}>
                  Verify
                </Button>
                <p className={styles.switchAuth}>
                  <button type="button" className={styles.linkBtn} onClick={backToPassword}>
                    Back to password
                  </button>
                </p>
              </form>
            </>
          ) : (
            <>
              <h1 className={styles.heading}>Sign in</h1>
              <p className={styles.sub}>Welcome back to your workspace.</p>
              <form onSubmit={submit} className={styles.form}>
              <TextField
                id="email"
                type="email"
                label="Email"
                autoComplete="email"
                leading={<EnvelopeIcon />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
              <TextField
                id="password"
                type="password"
                label="Password"
                autoComplete="current-password"
                leading={<LockIcon />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <div className={styles.forgotRow}>
                <Link to="/password-reset" className={styles.forgot}>Forgot password?</Link>
              </div>
              {error && <p className={styles.error}>{error}</p>}
              <Button type="submit" fullWidth isLoading={loading}>
                Sign in
              </Button>
              <div className={styles.divider}><span>or</span></div>
              <Button
                type="button"
                variant="gray"
                fullWidth
                leftIcon={<SlackIcon width={18} height={18} />}
                onClick={() => setError("Slack sign-in isn't enabled for this workspace yet.")}
              >
                Continue with Slack
              </Button>
              <p className={styles.switchAuth}>
                Need an account? <Link to="/register" className={styles.forgot}>Request access</Link>
              </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
